import type { AccessContext, ResolvedAccess, RouteAccessRule } from './routeAccessTypes';
import {
  canManageIntegrationAdmin,
  canManageSistemaUsers,
  canManageVisitas,
  effectiveUserRoles,
  effectiveUsername,
  isSuperAdminRole,
} from '../utils/sistemaAuthz';

export type { AccessContext, AccessDecision, ResolvedAccess, RouteAccessRule } from './routeAccessTypes';

const DENY_MESSAGES: Record<string, string> = {
  'admin.required': 'No tenés permisos para acceder a la administración de la empresa.',
  'superadmin.required': 'Esta sección está reservada para administradores del sistema (SUPERADMIN).',
  'visitas.manager.required': 'No tenés permisos para administrar el módulo de visitas con el rol actual.',
  'users.manage.required': 'No tenés permisos para gestionar usuarios.',
  'integration.tokens.required': 'No tenés permisos para administrar tokens de integración.',
  'generic.denied': 'No tenés permisos para acceder a esta sección.',
};

export function normalizePathname(pathname: string): string {
  if (!pathname || pathname === '/') return '/';
  const withoutTrailing = pathname.replace(/\/+$/, '') || '/';
  return withoutTrailing;
}

function matchesPattern(pathname: string, pattern: string, matchMode: 'prefix' | 'exact'): boolean {
  const path = normalizePathname(pathname);
  const pat = normalizePathname(pattern);
  if (matchMode === 'exact') return path === pat;
  return path === pat || path.startsWith(`${pat}/`);
}

function extractUsuarioParam(pathname: string): string | undefined {
  const match = pathname.match(/^\/sistema\/usuarios\/([^/]+)/i);
  if (!match?.[1]) return undefined;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

export function extractRouteParams(pathname: string): Record<string, string | undefined> {
  const usuario = extractUsuarioParam(pathname);
  return usuario ? { usuario } : {};
}

function requireAdmin(ctx: AccessContext): boolean {
  return canManageSistemaUsers(ctx.roles);
}

function requireSuperAdmin(ctx: AccessContext): boolean {
  return isSuperAdminRole(ctx.roles);
}

function requireVisitasManager(ctx: AccessContext): boolean {
  return canManageVisitas(ctx.roles);
}

function requireIntegrationAdmin(ctx: AccessContext): boolean {
  return canManageIntegrationAdmin(ctx.roles);
}

export function allowUsuarioForm(ctx: AccessContext): boolean {
  if (canManageSistemaUsers(ctx.roles)) return true;
  const usuario = ctx.params.usuario ?? extractUsuarioParam(ctx.pathname);
  if (!usuario || usuario.toLowerCase() === 'nuevo') return false;
  const self = ctx.username.trim();
  if (!self) return false;
  return usuario.toLowerCase() === self.toLowerCase();
}

function rule(
  id: string,
  pattern: string,
  priority: number,
  predicate: (ctx: AccessContext) => boolean,
  denyMessageKey: string,
  matchMode: 'prefix' | 'exact' = 'prefix',
): RouteAccessRule {
  return { id, pattern, matchMode, priority, predicate, denyMessageKey };
}

/** Catálogo central: primera regla que coincide (por prioridad) define allow/deny. */
export const ROUTE_ACCESS_RULES: RouteAccessRule[] = [
  rule('sistema.tenants', '/sistema/tenants', 100, requireSuperAdmin, 'superadmin.required'),
  rule('sistema.usuarios-maestros', '/sistema/usuarios-maestros', 100, requireSuperAdmin, 'superadmin.required'),
  rule('sistema.tipo-formulario', '/sistema/tipo-formulario', 95, requireSuperAdmin, 'superadmin.required'),
  rule('sistema.tipoformulario', '/sistema/tipoformulario', 95, requireSuperAdmin, 'superadmin.required'),
  rule('sistema.caja-usuario', '/sistema/caja-usuario', 95, requireSuperAdmin, 'superadmin.required'),
  rule('sistema.grupo-caja-usuario', '/sistema/grupo-caja-usuario', 95, requireSuperAdmin, 'superadmin.required'),
  rule('sistema.usuarios', '/sistema/usuarios', 90, allowUsuarioForm, 'users.manage.required'),
  rule('config.tokens', '/configuraciones/tokens-integracion', 85, requireIntegrationAdmin, 'integration.tokens.required'),
  rule('config.correo', '/configuraciones/correo-salida', 85, requireIntegrationAdmin, 'integration.tokens.required'),
  rule('config.tienda', '/configuraciones/tienda', 85, requireAdmin, 'admin.required'),
  rule('config.sucventa', '/configuraciones/sucventa', 80, requireSuperAdmin, 'superadmin.required'),
  rule('ventas.visitas', '/ventas/visitas', 75, requireVisitasManager, 'visitas.manager.required'),
  rule('config.hub', '/configuraciones', 70, requireAdmin, 'admin.required'),
  rule('sistema.companias', '/sistema/companias', 65, requireAdmin, 'admin.required'),
  rule('sistema.monedas', '/sistema/monedas', 65, requireAdmin, 'admin.required'),
  rule('sistema.movmoneda', '/sistema/movmoneda', 65, requireAdmin, 'admin.required'),
  rule('sistema.provincias', '/sistema/provincias', 65, requireAdmin, 'admin.required'),
  rule('sistema.localidades', '/sistema/localidades', 65, requireAdmin, 'admin.required'),
  rule('sistema.catch-all', '/sistema', 60, requireSuperAdmin, 'superadmin.required'),
];

const SORTED_RULES = [...ROUTE_ACCESS_RULES].sort((a, b) => {
  if (b.priority !== a.priority) return b.priority - a.priority;
  return b.pattern.length - a.pattern.length;
});

export function resolveRouteAccess(ctx: AccessContext): ResolvedAccess {
  for (const accessRule of SORTED_RULES) {
    if (!matchesPattern(ctx.pathname, accessRule.pattern, accessRule.matchMode)) continue;
    if (accessRule.predicate(ctx)) {
      return { decision: 'allow', ruleId: accessRule.id };
    }
    return {
      decision: 'deny',
      ruleId: accessRule.id,
      denyMessageKey: accessRule.denyMessageKey ?? 'generic.denied',
    };
  }
  return { decision: 'allow' };
}

export function getAccessContextFromAuth(
  pathname: string,
  params: Record<string, string | undefined>,
  user: { roles?: string[]; username?: string } | null | undefined,
): AccessContext {
  const mergedParams = { ...extractRouteParams(pathname), ...params };
  return {
    pathname: normalizePathname(pathname),
    params: mergedParams,
    roles: effectiveUserRoles(user?.roles),
    username: effectiveUsername(user?.username),
  };
}

/** Evita falso denegado o flash mientras se rehidratan roles tras F5. */
export function isAccessPending(isAuthenticated: boolean, roles: string[] | undefined): boolean {
  if (!isAuthenticated) return false;
  if (typeof window === 'undefined') return false;
  const token = localStorage.getItem('token');
  if (!token) return false;
  const effective = effectiveUserRoles(roles);
  return effective.length === 0;
}

export function getDenyMessage(denyMessageKey?: string): string {
  if (!denyMessageKey) return DENY_MESSAGES['generic.denied'];
  return DENY_MESSAGES[denyMessageKey] ?? DENY_MESSAGES['generic.denied'];
}
